import { createAsyncThunk } from '@reduxjs/toolkit';
import GroupAPI from './groupAPI';

// ------------------------- GROUPS -------------------------

export const fetchGroups = createAsyncThunk(
  'groups/fetchGroups',
  async ({ skip = 0, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      return await GroupAPI.fetchGroups(skip, limit);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchGroupById = createAsyncThunk(
  'groups/fetchGroupById',
  async (groupId, { rejectWithValue }) => {
    try {
      return await GroupAPI.fetchGroupById(groupId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchGroupByName = createAsyncThunk(
  'groups/fetchGroupByName',
  async (name, { rejectWithValue }) => {
    try {
      return await GroupAPI.fetchGroupByName(name);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createGroup = createAsyncThunk(
  'groups/createGroup',
  async (groupData, { rejectWithValue }) => {
    try {
      return await GroupAPI.createGroup(groupData);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateGroup = createAsyncThunk(
  'groups/updateGroup',
  async ({ groupId, groupData }, { rejectWithValue }) => {
    try {
      return await GroupAPI.updateGroup(groupId, groupData);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteGroup = createAsyncThunk(
  'groups/deleteGroup',
  async (groupId, { rejectWithValue }) => {
    try {
      return await GroupAPI.deleteGroup(groupId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ---------------------- GROUP MEMBERS ----------------------

export const fetchGroupMembers = createAsyncThunk(
  'groups/fetchGroupMembers',
  async (groupId, { rejectWithValue }) => {
    try {
      return await GroupAPI.fetchGroupMembers(groupId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addMemberToGroup = createAsyncThunk(
  'groups/addMemberToGroup',
  async ({ groupId, userId }, { rejectWithValue }) => {
    try {
      return await GroupAPI.addMemberToGroup(groupId, userId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const removeMemberFromGroup = createAsyncThunk(
  'groups/removeMemberFromGroup',
  async ({ groupId, userId }, { rejectWithValue }) => {
    try {
      return await GroupAPI.removeMemberFromGroup(groupId, userId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const joinGroup = createAsyncThunk(
  'groups/joinGroup',
  async (groupId, { rejectWithValue }) => {
    try {
      return await GroupAPI.joinGroup(groupId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const leaveGroup = createAsyncThunk(
  'groups/leaveGroup',
  async (groupId, { rejectWithValue }) => {
    try {
      await GroupAPI.leaveGroup(groupId);
      return groupId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const approveMember = createAsyncThunk(
  'groups/approveMember',
  async ({ groupId, userId }, { rejectWithValue }) => {
    try {
      return await GroupAPI.approveMember(groupId, userId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const rejectMember = createAsyncThunk(
  'groups/rejectMember',
  async ({ groupId, userId }, { rejectWithValue }) => {
    try {
      return await GroupAPI.rejectMember(groupId, userId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchMyGroupInvites = createAsyncThunk(
  'groups/fetchMyGroupInvites',
  async (_, { rejectWithValue }) => {
    try {
      return await GroupAPI.fetchMyGroupInvites();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const acceptGroupInvite = createAsyncThunk(
  'groups/acceptGroupInvite',
  async (groupId, { rejectWithValue }) => {
    try {
      const result = await GroupAPI.acceptGroupInvite(groupId);
      return { groupId, result };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const declineGroupInvite = createAsyncThunk(
  'groups/declineGroupInvite',
  async (groupId, { rejectWithValue }) => {
    try {
      await GroupAPI.declineGroupInvite(groupId);
      return { groupId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateMemberRole = createAsyncThunk(
  'groups/updateMemberRole',
  async ({ groupId, userId, newRole }, { rejectWithValue }) => {
    try {
      return await GroupAPI.updateMemberRole(groupId, userId, newRole);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const transferOwnership = createAsyncThunk(
  'groups/transferOwnership',
  async ({ groupId, newOwnerUserId }, { rejectWithValue }) => {
    try {
      return await GroupAPI.transferOwnership(groupId, newOwnerUserId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const muteMember = createAsyncThunk(
  'groups/muteMember',
  async ({ groupId, userId, reason, until }, { rejectWithValue }) => {
    try {
      return await GroupAPI.muteMember(groupId, userId, { reason, until });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const unmuteMember = createAsyncThunk(
  'groups/unmuteMember',
  async ({ groupId, userId }, { rejectWithValue }) => {
    try {
      return await GroupAPI.unmuteMember(groupId, userId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// -------------------- GROUP MESSAGES --------------------

export const createGroupMessage = createAsyncThunk(
  'groups/createGroupMessage',
  async ({ groupId, messageData }, { rejectWithValue }) => {
    try {
      return await GroupAPI.createGroupMessage(groupId, messageData);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchGroupMessages = createAsyncThunk(
  'groups/fetchGroupMessages',
  async ({ groupId, skip = 0, limit = 10 }, { rejectWithValue }) => {
    try {
      return await GroupAPI.fetchGroupMessages(groupId, skip, limit);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateGroupMessage = createAsyncThunk(
  'groups/updateGroupMessage',
  async ({ messageId, content }, { rejectWithValue }) => {
    try {
      return await GroupAPI.updateGroupMessage(messageId, content);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteGroupMessage = createAsyncThunk(
  'groups/deleteGroupMessage',
  async (messageId, { rejectWithValue }) => {
    try {
      return await GroupAPI.deleteGroupMessage(messageId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ---------------- GROUP DISPLAY PICTURES ----------------

export const fetchGroupDisplayPicture = createAsyncThunk(
  'groups/fetchGroupDisplayPicture',
  async (groupId, { rejectWithValue }) => {
    try {
      return await GroupAPI.fetchGroupDisplayPicture(groupId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const upsertGroupDisplayPicture = createAsyncThunk(
  'groups/upsertGroupDisplayPicture',
  async ({ groupId, pictureUrl }, { rejectWithValue }) => {
    try {
      return await GroupAPI.upsertGroupDisplayPicture(groupId, pictureUrl);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteGroupDisplayPicture = createAsyncThunk(
  'groups/deleteGroupDisplayPicture',
  async (groupId, { rejectWithValue }) => {
    try {
      return await GroupAPI.deleteGroupDisplayPicture(groupId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateGroupBackgroundTheme = createAsyncThunk(
  'groups/updateGroupBackgroundTheme',
  async ({ groupId, backgroundTheme }, { rejectWithValue }) => {
    try {
      return await GroupAPI.updateGroupBackgroundTheme(groupId, backgroundTheme);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// -------------------- SEARCH & ANALYTICS ----------------

export const searchUsers = createAsyncThunk(
  'groups/searchUsers',
  async ({ query, skip = 0, limit = 5 }, { rejectWithValue }) => {
    try {
      return await GroupAPI.searchUsers(query, skip, limit);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const searchGroupsByStock = createAsyncThunk(
  'groups/searchGroupsByStock',
  async ({ ticker, sector, industry, skip = 0, limit = 10 }, { rejectWithValue }) => {
    try {
      return await GroupAPI.searchGroupsByStock(ticker, sector, industry, skip, limit);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getGroupAnalytics = createAsyncThunk(
  'groups/getGroupAnalytics',
  async (groupId, { rejectWithValue }) => {
    try {
      return await GroupAPI.getGroupAnalytics(groupId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);